import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LanguageContext = createContext(null);

const dictionary = {
  en: {
    language: "Language",
    english: "English",
    arabic: "العربية",
    login: "Login",
    logout: "Log out",
    home: "Home",
    profiles: "Profiles",
    projects: "Projects",
    signupBusiness: "Business SignUp",
    signupProfessional: "Professional SignUp",
    invalidCredentials: "Invalid username or password.",
    signupReceived:
      "We have received your signup request and added you to the activation queue. Your account will be activated soon. Thank you for contacting us. For any inquiries, please contact support.",
    demoSubmission:
      "This is a preview environment. Your submission has been recorded for preview purposes only.",
    demoProfessional: "Access as Professional",
    demoBusiness: "Access as Business",
    about: "About",
    featured: "Featured",
    loadingHighlights: "Loading highlights...",
    welcomeToDevGenius: "Welcome to DevGenius",
    aboutText1: "DevGenius is your go-to platform for connecting developers, designers, and exciting projects in the tech industry. Whether you're looking to showcase your skills, find talented professionals, or collaborate on innovative projects, DevGenius has you covered.",
    aboutText2: "Our community brings together individuals from various backgrounds and expertise levels, fostering an environment of learning, growth, and collaboration. Join us today and take your tech career to the next level!",
    featureSmartMatching: "Smart Matching",
    featureVerifiedProfiles: "Verified Profiles",
    featureRealtimeCollab: "Real-time Collab",
    featureProjectTracking: "Project Tracking",
    footerTagline: "Connecting brilliant minds with ambitious projects. The future of tech collaboration starts here.",
    footerPlatform: "Platform",
    footerBrowseProfessionals: "Browse Professionals",
    footerExploreProjects: "Explore Projects",
    footerPostProject: "Post a Project",
    footerStatus: "Status",
    footerDemoModeActive: "Preview Mode Active",
    footerRights: "All rights reserved.",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",
    footerContact: "Contact",
    loginWelcomeBack: "Welcome Back",
    loginContinue: "Sign in to continue to DevGenius",
    emailAddress: "Email Address",
    password: "Password",
    emailPlaceholder: "you@example.com",
    passwordPlaceholder: "Enter your password",
    signingIn: "Signing in...",
    quickDemo: "Quick Demo Access",
    signupModalTitle: "Signup Request Received",
    validationTitle: "Validation",
    businessAccount: "Business Account",
    businessSignupTitle: "Business Signup",
    fullNameBusinessLabel: "Full Name / Business Name *",
    fullNameBusinessPlaceholder: "Enter your full name or business name",
    createSecurePassword: "Create a secure password",
    phoneNumberLabel: "Phone Number *",
    companyDetails: "Company Details",
    companyDescriptionLabel: "Company Description *",
    companyDescriptionPlaceholder: "Provide a brief description of your company",
    yearsInMarketLabel: "Years in the Market *",
    yearsInMarketPlaceholder: "Select how long your company has been in the market",
    lessThan1Year: "Less than 1 year",
    years1to3: "1-3 years",
    years3to5: "3-5 years",
    moreThan5Years: "More than 5 years",
    mandatoryFieldsNote: "Fields marked with * are mandatory",
    submitting: "Submitting...",
    signUpBusinessBtn: "Sign Up as Business",
    professionalSignUpTitle: "Professional Sign Up",
    professionalJoinText: "Join our network of verified professionals",
    fullNameLabel: "Full Name *",
    fullNamePlaceholder: "Enter your full name",
    emailAddressRequired: "Email Address *",
    transcriptUploadLabel: "Transcript/Certificate Upload (Max 5) *",
    addEducationDetails: "Add Your Education Details",
    editEducationDetails: "Edit Your Education Details",
    howManyEducationRecords: "How many educational records do you want to add?",
    educationRecordsPlaceholder: "Enter the number of records (e.g., 3)",
    institutionName: "Institution Name",
    institutionNamePlaceholder: "Enter institution name",
    degreeCertificateName: "Degree/Certificate Name",
    selectDegreeCertificate: "Select degree or certificate",
    bachelorsDegree: "Bachelor's Degree",
    mastersDegree: "Master's Degree",
    diploma: "Diploma",
    certificate: "Certificate",
    fieldOfStudy: "Field of Study",
    additionalNotesOptional: "Additional Notes (Optional)",
    additionalNotesPlaceholder: "Add any additional notes or achievements (optional)",
    updateEducationDetails: "Update Education Details",
    saveEducationDetails: "Save Education Details",
    educationSubmittedSuccess: "Education details submitted successfully!",
    educationUpdatedSuccess: "Education details updated successfully!",
    educationSubmitFailed: "Failed to submit education details.",
    educationSubmitError: "An error occurred while submitting education details.",
    skillsSubmittedSuccess: "Skills submitted successfully!",
    skillsSubmitFailed: "Failed to submit skills.",
    skillsSubmitError: "An error occurred while submitting skills.",
    workExperienceSubtitle: "Share your professional journey by adding your work experience details. This helps potential clients or employers better understand your expertise.",
    workExperienceSubmittedSuccess: "Work experience submitted successfully!",
    workExperienceUpdatedSuccess: "Work experience updated successfully!",
    workExperienceSubmitFailed: "Failed to submit work experience.",
    workExperienceSubmitError: "An error occurred while submitting work experience.",
    characters: "characters",
    completeProject: "Complete Project",
    rating: "Rating",
    comment: "Comment",
    dragDropHint: "Drag and drop files or click to select",
    personalImageUploadLabel: "Personal Image Upload *",
    chooseProfileDesignLabel: "Choose Profile Design *",
    profileOption: "Profile Option",
    signUpProfessionalBtn: "Sign Up as Professional",
    selectProfileValidation: "Please select a profile design option.",
    weak: "Weak",
    fair: "Fair",
    strong: "Strong",
    veryStrong: "Very Strong",
    filterProjects: "Filter Projects",
    fromDate: "From Date",
    toDate: "To Date",
    status: "Status",
    all: "All",
    unassigned: "Unassigned",
    assigned: "Assigned",
    completed: "Completed",
    private: "Private",
    close: "Close",
    loading: "Loading...",
    cancel: "Cancel",
    confirm: "Confirm",
    submit: "Submit",
    next: "Next",
    previous: "Previous",
    remove: "Remove",
    role: "Role",
    actions: "Actions",
    active: "Active",
    suspended: "Suspended",
    blocked: "Blocked",
    admin: "Admin",
    business: "Business",
    professional: "Professional",
    sidebarDashboard: "Dashboard",
    sidebarUserManagement: "User Management",
    sidebarAdvertisements: "Advertisements",
    sidebarMyProfile: "My Profile",
    sidebarViewProfile: "View Profile",
    sidebarAddProjects: "Add Projects",
    sidebarAddExperience: "Add Experience",
    sidebarAddEducation: "Add Education",
    sidebarAddSkills: "Add Skills",
    sidebarHome: "Home",
    sidebarProfiles: "Profiles",
    sidebarProjects: "Projects",
    sidebarMyProject: "My Project",
    sidebarLoggedInAs: "Logged in as",
    sidebarPanel: "panel",
    loginToDevGenius: "Login to DevGenius",
    clientIdMissing: "Client ID not found. Please log in.",
    alreadyRequestedProject: "You have already requested this project.",
    requestProjectFailed: "Failed to request project.",
    requestProjectSuccess: "Project requested successfully!",
    projectRequested: "Project requested",
    requestProjectError: "An error occurred while requesting the project.",
    projectDetails: "Project Details",
    priceRange: "Price Range",
    technologiesUsed: "Technologies Used",
    notAvailable: "N/A",
    projectType: "Project Type",
    completionDate: "Completion Date",
    projectProfessional: "Project Professional",
    projectImages: "Project Images",
    viewImages: "View Images",
    projectDescription: "Project Description",
    requestForProject: "Request for Project",
    addNote: "Add a Note",
    addNotePlaceholder: "Add a note for this project...",
    confirmUserRemoval: "Confirm User Removal",
    removeUserWarning: "Are you sure you want to remove this user? This action cannot be undone.",
    editAdvertisement: "Edit Advertisement",
    addNewAdvertisement: "Add New Advertisement",
    title: "Title",
    description: "Description",
    confirmRemoveAd: "Are you sure you want to remove this ad?",
    manageAdvertisements: "Manage Your Advertisements",
    userManagementDashboard: "User Management Dashboard",
    searchUsersPlaceholder: "Search users by name or email...",
    filterByRole: "Filter by Role",
    filterByStatus: "Filter by Status",
    userId: "User ID",
    fullName: "Full Name",
    removeUser: "Remove User",
    userGeneric: "User",
  },
  ar: {
    language: "اللغة",
    english: "English",
    arabic: "العربية",
    login: "تسجيل الدخول",
    logout: "تسجيل الخروج",
    home: "الرئيسية",
    profiles: "الملفات",
    projects: "المشاريع",
    signupBusiness: "تسجيل الأعمال",
    signupProfessional: "تسجيل المحترف",
    invalidCredentials: "اسم المستخدم أو كلمة المرور غير صحيحة.",
    signupReceived:
      "لقد استلمنا طلب التسجيل الخاص بك وأضفناك إلى قائمة التفعيل. سيتم تفعيل حسابك قريبًا. شكرًا لتواصلك معنا. لأي استفسار، يرجى التواصل مع الدعم.",
    demoSubmission:
      "هذه بيئة معاينة. تم تسجيل عملية الإرسال لأغراض المعاينة فقط.",
    demoProfessional: "الدخول كمحترف",
    demoBusiness: "الدخول كشركة",
    about: "حول",
    featured: "مميز",
    loadingHighlights: "جارٍ تحميل أبرز المحتويات...",
    welcomeToDevGenius: "مرحبًا بك في DevGenius",
    aboutText1: "DevGenius هي منصتك المفضلة لربط المطورين والمصممين بالمشاريع التقنية المميزة. سواء كنت ترغب في عرض مهاراتك، أو العثور على محترفين موهوبين، أو التعاون في مشاريع مبتكرة، فكل ما تحتاجه هنا.",
    aboutText2: "يجمع مجتمعنا أفرادًا من خلفيات ومستويات خبرة متنوعة، مما يخلق بيئة للتعلم والنمو والتعاون. انضم إلينا اليوم وارتقِ بمسيرتك التقنية إلى مستوى جديد.",
    featureSmartMatching: "مطابقة ذكية",
    featureVerifiedProfiles: "ملفات موثقة",
    featureRealtimeCollab: "تعاون لحظي",
    featureProjectTracking: "متابعة المشاريع",
    footerTagline: "نربط العقول المبدعة بالمشاريع الطموحة. مستقبل التعاون التقني يبدأ من هنا.",
    footerPlatform: "المنصة",
    footerBrowseProfessionals: "تصفح المحترفين",
    footerExploreProjects: "استكشف المشاريع",
    footerPostProject: "أضف مشروعًا",
    footerStatus: "الحالة",
    footerDemoModeActive: "وضع المعاينة مفعل",
    footerRights: "جميع الحقوق محفوظة.",
    footerPrivacy: "الخصوصية",
    footerTerms: "الشروط",
    footerContact: "تواصل",
    loginWelcomeBack: "مرحبًا بعودتك",
    loginContinue: "سجّل الدخول للمتابعة إلى DevGenius",
    emailAddress: "البريد الإلكتروني",
    password: "كلمة المرور",
    emailPlaceholder: "you@example.com",
    passwordPlaceholder: "أدخل كلمة المرور",
    signingIn: "جارٍ تسجيل الدخول...",
    quickDemo: "دخول عرض تجريبي سريع",
    signupModalTitle: "تم استلام طلب التسجيل",
    validationTitle: "التحقق",
    businessAccount: "حساب أعمال",
    businessSignupTitle: "تسجيل الأعمال",
    fullNameBusinessLabel: "الاسم الكامل / اسم الشركة *",
    fullNameBusinessPlaceholder: "أدخل اسمك الكامل أو اسم شركتك",
    createSecurePassword: "أنشئ كلمة مرور قوية",
    phoneNumberLabel: "رقم الهاتف *",
    companyDetails: "بيانات الشركة",
    companyDescriptionLabel: "وصف الشركة *",
    companyDescriptionPlaceholder: "اكتب وصفًا مختصرًا عن شركتك",
    yearsInMarketLabel: "سنوات التواجد في السوق *",
    yearsInMarketPlaceholder: "اختر مدة تواجد شركتك في السوق",
    lessThan1Year: "أقل من سنة",
    years1to3: "1-3 سنوات",
    years3to5: "3-5 سنوات",
    moreThan5Years: "أكثر من 5 سنوات",
    mandatoryFieldsNote: "الحقول المعلّمة بـ * إلزامية",
    submitting: "جارٍ الإرسال...",
    signUpBusinessBtn: "سجّل كجهة أعمال",
    professionalSignUpTitle: "تسجيل المحترف",
    professionalJoinText: "انضم إلى شبكتنا من المحترفين الموثقين",
    fullNameLabel: "الاسم الكامل *",
    fullNamePlaceholder: "أدخل اسمك الكامل",
    emailAddressRequired: "البريد الإلكتروني *",
    transcriptUploadLabel: "رفع الشهادة/الوثيقة (الحد الأقصى 5) *",
    addEducationDetails: "أضف تفاصيل تعليمك",
    editEducationDetails: "عدّل تفاصيل تعليمك",
    howManyEducationRecords: "كم سجلًا تعليميًا تريد إضافته؟",
    educationRecordsPlaceholder: "أدخل عدد السجلات (مثال: 3)",
    institutionName: "اسم المؤسسة",
    institutionNamePlaceholder: "أدخل اسم المؤسسة",
    degreeCertificateName: "اسم الدرجة/الشهادة",
    selectDegreeCertificate: "اختر الدرجة أو الشهادة",
    bachelorsDegree: "درجة البكالوريوس",
    mastersDegree: "درجة الماجستير",
    diploma: "دبلوم",
    certificate: "شهادة",
    fieldOfStudy: "مجال الدراسة",
    additionalNotesOptional: "ملاحظات إضافية (اختياري)",
    additionalNotesPlaceholder: "أضف أي ملاحظات أو إنجازات إضافية (اختياري)",
    updateEducationDetails: "تحديث تفاصيل التعليم",
    saveEducationDetails: "حفظ تفاصيل التعليم",
    educationSubmittedSuccess: "تم إرسال تفاصيل التعليم بنجاح!",
    educationUpdatedSuccess: "تم تحديث تفاصيل التعليم بنجاح!",
    educationSubmitFailed: "فشل إرسال تفاصيل التعليم.",
    educationSubmitError: "حدث خطأ أثناء إرسال تفاصيل التعليم.",
    skillsSubmittedSuccess: "تم إرسال المهارات بنجاح!",
    skillsSubmitFailed: "فشل إرسال المهارات.",
    skillsSubmitError: "حدث خطأ أثناء إرسال المهارات.",
    workExperienceSubtitle: "شارك رحلتك المهنية عبر إضافة تفاصيل خبراتك العملية. هذا يساعد العملاء أو أصحاب العمل المحتملين على فهم خبرتك بشكل أفضل.",
    workExperienceSubmittedSuccess: "تم إرسال الخبرة العملية بنجاح!",
    workExperienceUpdatedSuccess: "تم تحديث الخبرة العملية بنجاح!",
    workExperienceSubmitFailed: "فشل إرسال الخبرة العملية.",
    workExperienceSubmitError: "حدث خطأ أثناء إرسال الخبرة العملية.",
    characters: "حرف",
    completeProject: "إكمال المشروع",
    rating: "التقييم",
    comment: "التعليق",
    dragDropHint: "اسحب الملفات هنا أو اضغط للاختيار",
    personalImageUploadLabel: "رفع الصورة الشخصية *",
    chooseProfileDesignLabel: "اختر تصميم الملف الشخصي *",
    profileOption: "الخيار",
    signUpProfessionalBtn: "سجّل كمحترف",
    selectProfileValidation: "يرجى اختيار تصميم للملف الشخصي.",
    weak: "ضعيف",
    fair: "متوسط",
    strong: "قوي",
    veryStrong: "قوي جدًا",
    filterProjects: "تصفية المشاريع",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    status: "الحالة",
    all: "الكل",
    unassigned: "غير مُسند",
    assigned: "مُسند",
    completed: "مكتمل",
    private: "خاص",
    close: "إغلاق",
    loading: "جارٍ التحميل...",
    cancel: "إلغاء",
    confirm: "تأكيد",
    submit: "إرسال",
    next: "التالي",
    previous: "السابق",
    remove: "إزالة",
    role: "الدور",
    actions: "الإجراءات",
    active: "نشط",
    suspended: "موقوف",
    blocked: "محظور",
    admin: "مدير",
    business: "شركة",
    professional: "محترف",
    sidebarDashboard: "لوحة التحكم",
    sidebarUserManagement: "إدارة المستخدمين",
    sidebarAdvertisements: "الإعلانات",
    sidebarMyProfile: "ملفي الشخصي",
    sidebarViewProfile: "عرض الملف الشخصي",
    sidebarAddProjects: "إضافة مشاريع",
    sidebarAddExperience: "إضافة خبرة",
    sidebarAddEducation: "إضافة تعليم",
    sidebarAddSkills: "إضافة مهارات",
    sidebarHome: "الرئيسية",
    sidebarProfiles: "الملفات",
    sidebarProjects: "المشاريع",
    sidebarMyProject: "مشاريعي",
    sidebarLoggedInAs: "تم تسجيل الدخول باسم",
    sidebarPanel: "لوحة",
    loginToDevGenius: "تسجيل الدخول إلى DevGenius",
    clientIdMissing: "تعذر العثور على معرف العميل. يرجى تسجيل الدخول.",
    alreadyRequestedProject: "لقد طلبت هذا المشروع مسبقًا.",
    requestProjectFailed: "فشل طلب المشروع.",
    requestProjectSuccess: "تم طلب المشروع بنجاح!",
    projectRequested: "تم طلب المشروع",
    requestProjectError: "حدث خطأ أثناء طلب المشروع.",
    projectDetails: "تفاصيل المشروع",
    priceRange: "نطاق السعر",
    technologiesUsed: "التقنيات المستخدمة",
    notAvailable: "غير متوفر",
    projectType: "نوع المشروع",
    completionDate: "تاريخ الإنجاز",
    projectProfessional: "المحترف المنفذ",
    projectImages: "صور المشروع",
    viewImages: "عرض الصور",
    projectDescription: "وصف المشروع",
    requestForProject: "طلب المشروع",
    addNote: "أضف ملاحظة",
    addNotePlaceholder: "أضف ملاحظة لهذا المشروع...",
    confirmUserRemoval: "تأكيد إزالة المستخدم",
    removeUserWarning: "هل أنت متأكد من إزالة هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.",
    editAdvertisement: "تعديل الإعلان",
    addNewAdvertisement: "إضافة إعلان جديد",
    title: "العنوان",
    description: "الوصف",
    confirmRemoveAd: "هل أنت متأكد من إزالة هذا الإعلان؟",
    manageAdvertisements: "إدارة إعلاناتك",
    userManagementDashboard: "لوحة إدارة المستخدمين",
    searchUsersPlaceholder: "ابحث عن مستخدم بالاسم أو البريد الإلكتروني...",
    filterByRole: "تصفية حسب الدور",
    filterByStatus: "تصفية حسب الحالة",
    userId: "معرف المستخدم",
    fullName: "الاسم الكامل",
    removeUser: "إزالة المستخدم",
    userGeneric: "مستخدم",
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem("devgenius_language") || "en");

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  const setCurrentLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    localStorage.setItem("devgenius_language", nextLanguage);
    document.documentElement.lang = nextLanguage;
    document.documentElement.dir = nextLanguage === "ar" ? "rtl" : "ltr";
  };

  const value = useMemo(() => ({
    language,
    setLanguage: setCurrentLanguage,
    t: (key) => dictionary[language]?.[key] || key,
    isArabic: language === "ar",
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }
  return context;
};
